import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import { format, parseISO } from 'date-fns';

import type { SelectOption } from '@/components';
import type { BookingReservationDetail } from '@/types/api/queries';

import {
  BookingDialogBody,
  BookingDialogFooter,
  DatePicker,
  Select,
  SlotPicker,
} from '@/components';

import { bookingSpecialistAvailabilityQueries } from '@/queries';

import { useRescheduleBookingReservationMutation } from '@/mutations';

import { notifyBookingSuccess, notifyMutationError } from '@/helpers';

import { SEND_DATES_FORMAT } from '@/constants/dates';

type EditReservationFormProps = {
  reservation: BookingReservationDetail;
  reservationsUrl: string;
  specialistAvailabilityUrl: string;
  staff: SelectOption[];
  locations: SelectOption[];
  onClose: () => void;
  onSuccess: () => void;
};

export const EditReservationForm = (props: EditReservationFormProps) => {
  const {
    reservation,
    reservationsUrl,
    specialistAvailabilityUrl,
    staff,
    locations,
    onClose,
    onSuccess,
  } = props;
  const { t } = useTranslation();

  // Only a personal booking is rescheduled here, and one always names a specialist.
  const [staffId, setStaffId] = useState(String(reservation.staff?.id ?? ''));
  const [locationId, setLocationId] = useState(String(reservation.location.id));
  const [date, setDate] = useState(
    format(parseISO(reservation.starts_at), SEND_DATES_FORMAT),
  );
  const [startsAt, setStartsAt] = useState<string | null>(null);

  const onStaffChange = useCallback((value: string) => {
    setStaffId(value);
    setStartsAt(null);
  }, []);

  const onLocationChange = useCallback((value: string) => {
    setLocationId(value);
    setStartsAt(null);
  }, []);

  const onDateChange = useCallback((value: string) => {
    setDate(value);
    setStartsAt(null);
  }, []);

  const { data: availability, isLoading: isAvailabilityLoading } = useQuery(
    bookingSpecialistAvailabilityQueries.slots({
      url: specialistAvailabilityUrl,
      locationId: Number(locationId) || null,
      staffId: Number(staffId) || null,
      serviceId: reservation.service.id,
      bookingType: 'personal',
      date,
    }),
  );

  const { mutate: reschedule, isPending } =
    useRescheduleBookingReservationMutation();

  const onSave = useCallback(() => {
    if (!startsAt) {
      return;
    }
    reschedule(
      {
        url: `${reservationsUrl}/${reservation.id}`,
        data: {
          staff_id: Number(staffId),
          location_id: Number(locationId),
          starts_at: startsAt,
        },
      },
      {
        onSuccess: (response) => {
          notifyBookingSuccess(response);
          onSuccess();
          onClose();
        },
        onError: notifyMutationError,
      },
    );
  }, [
    locationId,
    onClose,
    onSuccess,
    reschedule,
    reservation.id,
    reservationsUrl,
    staffId,
    startsAt,
  ]);

  return (
    <>
      <BookingDialogBody>
        <p className="text-sm font-medium">{t('Edit booking')}</p>

        <Select
          label={t('Trainer')}
          value={staffId}
          onValueChange={onStaffChange}
          options={staff}
          searchable
        />
        <Select
          label={t('Location')}
          value={locationId}
          onValueChange={onLocationChange}
          options={locations}
        />
        <DatePicker
          label={t('Date')}
          value={date}
          onChange={onDateChange}
        />

        <SlotPicker
          label={t('Free start times')}
          slots={availability?.slots ?? []}
          value={startsAt}
          isLoading={isAvailabilityLoading}
          onChange={setStartsAt}
        />
      </BookingDialogBody>

      <BookingDialogFooter
        submitLabel={t('Save')}
        isPending={isPending}
        isSubmitDisabled={!startsAt}
        onClose={onClose}
        onSubmit={onSave}
      />
    </>
  );
};
